import { type ClientOptions, type Host } from '@algolia/client-common'
import { type LiteClient, liteClient } from 'algoliasearch/lite'
import { useMemo } from 'react'

import { useAdServerConfigurationContext } from '../../ad-server'
import { useExperimentsContext } from '../../experiments/components/ExperimentsContext'
import { experimentAccessorIsAdServerEnabled } from '../../experiments/model/experimentAccessor'
import { processEnvAccessorGetRetailMediaAlgoliaProxyServerUrl } from '../../process-env'
import { isServerSide } from '../../request-context'
import { useAlgoliaConfigContext } from '../context/algoliaConfigContext'

type AdServerProxyQueryParams = {
  // set flag to '1' for enabling ad server enrichment within algolia proxy
  adServer?: '1'
  // browser conversion id
  asbidc?: string
  // browser targeting id
  asbidt?: string
  expa?: string
  language: string
  pageType: string
  tenant: string
}

/**
 * Algolia request is being sent to proxy service
 * Proxy service gets sponsored hits from retail media ad server
 * Sponsored hits are added into original response
 */
export const useProxySearchClient = (): LiteClient => {
  const { algoliaApiKey, algoliaApplicationId, algoliaProxyClientUrl } =
    useAlgoliaConfigContext()
  const { experiments } = useExperimentsContext()
  const isAdServerEnabled = experimentAccessorIsAdServerEnabled(experiments)
  const { adServerTargeting } = useAdServerConfigurationContext()

  return useMemo(() => {
    const {
      context: { expa, language, pageType, tenant },
      user: { asbidc, asbidt },
    } = adServerTargeting

    const queryParameters: AdServerProxyQueryParams = {
      asbidc,
      asbidt,
      expa,
      language,
      pageType,
      tenant,
    }

    if (isAdServerEnabled) {
      queryParameters.adServer = '1'
    }

    const { hostname, protocol } = new URL(
      isServerSide()
        ? processEnvAccessorGetRetailMediaAlgoliaProxyServerUrl()
        : algoliaProxyClientUrl,
    )

    const options: ClientOptions = {
      baseQueryParameters: queryParameters,
      hosts: [
        {
          accept: 'read',
          /**
           * Remove the trailing colon from the protocol string
           * Assert it as a valid 'protocol' type defined in the Host interface.
           */
          protocol: protocol.slice(0, -1) as Host['protocol'],
          url: hostname,
        },
        // fallback to default Algolia host
        {
          accept: 'read',
          protocol: 'https',
          url: `${algoliaApplicationId}-dsn.algolia.net`,
        },
      ],
    }

    return liteClient(algoliaApplicationId, algoliaApiKey, options)
  }, [
    algoliaApiKey,
    algoliaApplicationId,
    algoliaProxyClientUrl,
    adServerTargeting,
    isAdServerEnabled,
  ])
}
